import { CommandPalette } from "@design";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { useCommandPaletteLabels } from "@/components/designLabels";
import { useCommandCategories } from "./useCommandCategories";

const PINS_KEY = "anthm.commandPalette.pins";
const DEFAULT_PINS = ["action:new-chat", "action:spaces", "action:settings"];

const OpenContext = createContext<() => void>(() => {});

/** Opens the command palette (sidebar search). */
export const useOpenCommandPalette = () => useContext(OpenContext);

function readPins(): string[] {
  try {
    const stored = JSON.parse(localStorage.getItem(PINS_KEY) ?? "null");
    return Array.isArray(stored) ? stored.filter((v) => typeof v === "string") : DEFAULT_PINS;
  } catch {
    return DEFAULT_PINS;
  }
}

/** The ⌘K command palette, shared by the desktop sidebar and the mobile drawer. */
export function CommandPaletteProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen(true);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const openPalette = useCallback(() => setOpen(true), []);

  return (
    <OpenContext.Provider value={openPalette}>
      {children}
      {open && <Palette onClose={() => setOpen(false)} />}
    </OpenContext.Provider>
  );
}

function Palette({ onClose }: Readonly<{ onClose: () => void }>) {
  const labels = useCommandPaletteLabels();
  const categories = useCommandCategories();
  const [pins, setPins] = useState(readPins);
  const savePins = (values: string[]) => {
    setPins(values);
    localStorage.setItem(PINS_KEY, JSON.stringify(values));
  };
  return (
    <CommandPalette
      open
      onClose={onClose}
      categories={categories}
      pinnedValues={pins}
      onPinnedValuesChange={savePins}
      labels={labels}
    />
  );
}
